import { useEffect, useRef, useState } from 'react';
import { Button, Input } from '@react-apps/design-system';
import { RegisterOfferModal } from './RegisterOfferModal';
import { useOfferSearch } from './use-offer-search';
import type { OfferOption } from './offers-api.datasource';

interface OfferSearchInputProps {
  label?: string;
  id: string;
  onSelect: (offer: OfferOption) => void;
  /** Erro de quem usa a oferta escolhida (ex.: já está em outra campanha). */
  error?: string;
  /** Escolha anterior gravando. `readOnly` mantém o foco para corrigir depois. */
  readOnly?: boolean;
  /** O operador voltou a digitar: quem usa limpa o `error` da escolha anterior. */
  onTermChange?: () => void;
}

/**
 * Escolhe uma oferta da `sales_offers` pelo UUID do Guru — da oferta ou do
 * produto. Busca exata: o operador cola o id do painel do provedor.
 *
 * Quando o Sales não conhece o id (oferta que ainda não vendeu), oferece
 * cadastrá-la conferindo no provedor; cadastrada, ela volta como escolhida.
 *
 * A lista é montada à mão pelo mesmo motivo do `ContactSearchInput`: precisa
 * abrir enquanto se digita.
 */
export function OfferSearchInput({
  label = 'Oferta',
  id,
  onSelect,
  error,
  readOnly,
  onTermChange,
}: OfferSearchInputProps) {
  const [term, setTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [registering, setRegistering] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { options, isLoading, isIncomplete, isEmpty, isError } =
    useOfferSearch(term);

  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isOpen]);

  const select = (offer: OfferOption) => {
    onSelect(offer);
    setTerm('');
    setIsOpen(false);
  };

  const showPanel =
    isOpen && (isLoading || isEmpty || isError || options.length > 0);

  return (
    <div
      ref={containerRef}
      className="relative"
      // O foco que sai pelo teclado fecha. Sem `relatedTarget` não: no Safari o
      // clique não foca o botão, e fechar aqui engoliria o clique — o
      // `mousedown` de fora cobre esse caso.
      onBlur={(e) => {
        const next = e.relatedTarget as Node | null;
        if (next && !e.currentTarget.contains(next)) setIsOpen(false);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setIsOpen(false);
      }}
    >
      <Input
        id={id}
        label={label}
        autoComplete="off"
        placeholder="Cole o id da oferta ou do produto no Guru"
        value={term}
        readOnly={readOnly}
        error={error}
        onChange={(e) => {
          setTerm(e.target.value);
          setIsOpen(true);
          onTermChange?.();
        }}
        onFocus={() => setIsOpen(true)}
        helperText={
          isIncomplete
            ? 'A busca precisa do id completo (UUID), como aparece no painel do Guru.'
            : undefined
        }
      />

      {showPanel && (
        <div className="border-(--color-border) bg-(--color-bg-primary) absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border shadow-lg">
          {isLoading ? (
            <p
              role="status"
              className="text-(--color-text-secondary) px-3 py-2 text-xs"
            >
              Buscando…
            </p>
          ) : isError ? (
            <p
              role="status"
              className="px-3 py-2 text-xs text-red-600 dark:text-red-400"
            >
              Não foi possível buscar agora. Tente de novo.
            </p>
          ) : isEmpty ? (
            <div className="space-y-2 px-3 py-3">
              <p
                role="status"
                className="text-(--color-text-secondary) text-sm"
              >
                Nenhuma oferta com este id no Sales.
              </p>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => {
                  setIsOpen(false);
                  setRegistering(true);
                }}
              >
                Cadastrar oferta
              </Button>
            </div>
          ) : (
            <ul>
              {options.map((offer) => (
                <li key={offer.id}>
                  <button
                    type="button"
                    onClick={() => select(offer)}
                    className="text-(--color-text-primary) hover:bg-(--color-bg-secondary) block w-full px-3 py-2 text-left text-sm transition-colors"
                  >
                    <span className="block truncate">{offer.name}</span>
                    <span className="text-(--color-text-secondary) block truncate text-xs">
                      {offer.productName ?? 'Produto sem nome'} ·{' '}
                      {offer.providerAccountName ?? offer.providerAccountId}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <RegisterOfferModal
        open={registering}
        onClose={() => setRegistering(false)}
        onRegistered={(offer) => {
          setRegistering(false);
          select(offer);
        }}
      />
    </div>
  );
}
